import DataTableColumnHeader from "@/components/Common/DataTable/DataTableColumnHeader";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SCENARIO_DECISION_LABELS, ScenarioDecision } from "@/consts/Scenario";
import { formatDate } from "@/libs/utils";
import { ScenarioApproval } from "@/types/scenario-approvals";
import { ColumnDef } from "@tanstack/react-table";
import { ReactComponent as TableSvg } from "@/assets/images/table.svg";

type Props = {
  onRowClick: (projectGroupId: string) => void;
  onTableClick: (approvalId: string) => void;
};
export default function createColumns({
  onRowClick,
  onTableClick,
}: Props): ColumnDef<ScenarioApproval>[] {
  return [
    {
      accessorKey: "title",
      header: ({ column }) => (
        <DataTableColumnHeader title="승인 시나리오" column={column} />
      ),
      cell: ({ row }) => {
        const { title } = row.original;
        return (
          <span className="text-gray-A text-sm font-normal truncate">
            {title}
          </span>
        );
      },
      enableSorting: false,
      size: 180,
    },
    {
      id: "scenario_detail",
      header: ({ column }) => (
        <DataTableColumnHeader title="시나리오 상세 보기" column={column} />
      ),
      cell: ({ row }) => {
        const { approval_id } = row.original;
        return (
          <div className="flex justify-start">
            <TableSvg
              className="w-5 h-5 p-1 shrink-0 bg-dark-red rounded-sm cursor-pointer"
              onClick={() => onTableClick(approval_id)}
            />
          </div>
        );
      },
      enableSorting: false,
      size: 120,
      meta: {
        isInteractive: true,
      },
    },
    {
      accessorKey: "project_group_title",
      header: ({ column }) => (
        <DataTableColumnHeader title="소속 프로젝트" column={column} />
      ),
      cell: ({ row, getValue }) => {
        const { project_group_id, project_group_title } = row.original;

        return (
          <button
            onClick={() => onRowClick(project_group_id)}
            className="text-gray-A text-sm font-normal text-left"
          >
            {project_group_title}
          </button>
        );
      },
      enableSorting: false,
      size: 380,
    },
    {
      accessorKey: "requested_by",
      header: ({ column }) => (
        <DataTableColumnHeader title="요청자" column={column} />
      ),
      cell: ({ row }) => {
        const { user_name, img_url, user_id } = row.original.requested_by;
        return (
          <div
            className="flex items-center gap-2"
            key={`${row.original.approval_id}-${user_id}`}
          >
            <Avatar className="size-4">
              <AvatarImage src={img_url} />
              <AvatarFallback>{user_name}</AvatarFallback>
            </Avatar>
            <span className="text-gray-A text-xs font-normal">{user_name}</span>
          </div>
        );
      },
      size: 230,
      enableSorting: false,
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader title="승인 요청 시점" column={column} />
      ),
      cell: ({ row }) => (
        <span className="text-gray-A text-sm font-normal">
          {formatDate(row.original.created_at, "yyyy-MM-dd")}
        </span>
      ),
      size: 230,
      enableSorting: false,
    },
    {
      accessorKey: "current_stage",
      header: ({ column }) => (
        <DataTableColumnHeader title="승인 상태" column={column} />
      ),
      cell: ({ row }) => {
        const { current_stage, final_decision } = row.original;

        if (final_decision === ScenarioDecision.APPROVED) {
          return (
            <span className="text-green text-[10px] font-normal">
              {SCENARIO_DECISION_LABELS[final_decision]}됨
            </span>
          );
        } else if (final_decision === ScenarioDecision.REJECTED) {
          return (
            <span className="text-pink text-[10px] font-normal">
              {SCENARIO_DECISION_LABELS[final_decision]}됨
            </span>
          );
        } else if (final_decision === ScenarioDecision.CANCELED) {
          return (
            <span className="text-pink text-[10px] font-normal">
              {SCENARIO_DECISION_LABELS[final_decision]}됨
            </span>
          );
        } else {
          return (
            <span className="text-[13px] text-gray-A font-normal">
              {current_stage}단계
            </span>
          );
        }
      },
      size: 230,
    },
    {
      accessorKey: "actions",
      header: ({ column }) => (
        <DataTableColumnHeader title="" column={column} />
      ),
      cell: ({ row }) => {
        return <></>;
      },
      size: 230,
      enableSorting: false,
    },
  ];
}
